import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import { useLazyQuery, useQuery } from '@apollo/client';
import {
  ChevronRight,
  CheckCircle2,
  EllipsisVertical,
  MapPin,
  Pencil,
  Plus,
  XCircle,
} from 'lucide-react';
import Select from 'react-select';
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { authContext } from 'contexts/AuthContext';
import {
  axiosDelete,
  axiosGetAPI,
  axiosPatchAPI,
  axiosPost,
  axiosUpdate,
} from 'services';
import {
  CITY_BY_STATE,
  STATES_BY_COUNTRY,
} from 'pages/MarketListing/MarketListingGraphQL';
import ServiceableLocationsModal from './ServiceableLocationsModal';
import './style.css';

const INDIA_COUNTRY_ID = '101';
const STATE_SELECT_STYLES = {
  control: (base) => ({
    ...base,
    minHeight: 42,
    borderColor: '#a8a29e',
    borderRadius: 10,
    fontSize: 14,
    boxShadow: 'none',
  }),
  menu: (base) => ({ ...base, zIndex: 2000, fontSize: 14 }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};

const CITY_STATUSES_STORAGE_KEY = 'polystox_city_master_statuses';

const getInitialCityStatuses = () => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(CITY_STATUSES_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const CityMaster = () => {
  const { auth } = useContext(authContext);
  const [selectedStateId, setSelectedStateId] = useState(null);
  const [openCityMenuId, setOpenCityMenuId] = useState(null);
  const [isAddCityOpen, setIsAddCityOpen] = useState(false);
  const [formStateId, setFormStateId] = useState('');
  const [cityName, setCityName] = useState('');
  const [editingCity, setEditingCity] = useState(null);
  const [editCityName, setEditCityName] = useState('');
  const [isCreatingCity, setIsCreatingCity] = useState(false);
  const [isUpdatingCity, setIsUpdatingCity] = useState(false);
  const [serviceableCity, setServiceableCity] = useState(null);
  const [serviceableCounts, setServiceableCounts] = useState({});
  const [cityStatuses, setCityStatuses] = useState(getInitialCityStatuses);
  const [updatingCityStatuses, setUpdatingCityStatuses] = useState({});

  const {
    data: statesData,
    loading: statesLoading,
    error: statesError,
  } = useQuery(STATES_BY_COUNTRY, {
    variables: { country: INDIA_COUNTRY_ID },
    fetchPolicy: 'network-only',
  });

  const [fetchCities, { data: citiesData, loading: citiesLoading, error: citiesError }] =
    useLazyQuery(CITY_BY_STATE, { fetchPolicy: 'network-only' });

  const states = useMemo(
    () =>
      [...(statesData?.statesByCountry || [])].sort((a, b) =>
        a.stateName.localeCompare(b.stateName),
      ),
    [statesData?.statesByCountry],
  );

  const cities = useMemo(
    () =>
      [...(citiesData?.citiesByState || [])].sort((a, b) =>
        a.cityName.localeCompare(b.cityName),
      ),
    [citiesData?.citiesByState],
  );

  useEffect(() => {
    if (cities.length === 0) return undefined;

    let isMounted = true;
    const cityCounts = {};

    const loadCityCounts = async () => {
      const loadBatch = async (startIndex) => {
        const batch = cities.slice(startIndex, startIndex + 8);
        await Promise.all(
          batch.map(async (city) => {
            try {
              const response = await axiosGetAPI(
                `${process.env.REACT_APP_REST_API_ENDPOINT}common/serviceable-locations/${city.id}`,
                auth,
              );
              const selectedCount = response?.data?.data?.selectedCount;

              if (
                response?.isSuccessful &&
                !response?.data?.error &&
                Number.isFinite(Number(selectedCount))
              ) {
                cityCounts[String(city.id)] = Number(selectedCount);
              }
            } catch {
              // Keep the count unavailable when its lookup fails.
            }
          }),
        );

        const nextStartIndex = startIndex + batch.length;
        if (nextStartIndex < cities.length) {
          return loadBatch(nextStartIndex);
        }
      };

      await loadBatch(0);

      if (isMounted) {
        setServiceableCounts((currentCounts) => ({
          ...currentCounts,
          ...cityCounts,
        }));
      }
    };

    loadCityCounts();

    return () => {
      isMounted = false;
    };
  }, [auth, cities]);
  const stateOptions = useMemo(
    () =>
      states.map((state) => ({
        value: String(state.id),
        label: state.stateName,
      })),
    [states],
  );
  const selectedFormState = stateOptions.find(
    (state) => state.value === formStateId,
  ) || null;

  const selectedState = states.find(
    (state) => String(state.id) === selectedStateId,
  );

  const selectState = useCallback(
    (state) => {
      const stateId = String(state.id);
      setSelectedStateId(stateId);
      fetchCities({ variables: { state: stateId } });
    },
    [fetchCities],
  );

  useEffect(() => {
    if (states.length > 0 && !selectedStateId) {
      selectState(states[0]);
    }
  }, [selectState, selectedStateId, states]);

  useEffect(() => {
    const closeMenuOnOutsideClick = (event) => {
      if (
        !event.target.closest('.city-service-city-row') &&
        !event.target.closest('.city-master-city-menu')
      ) {
        setOpenCityMenuId(null);
      }
    };

    document.addEventListener('click', closeMenuOnOutsideClick);
    return () => document.removeEventListener('click', closeMenuOnOutsideClick);
  }, []);

  const openAddCity = () => {
    const defaultState =
      states.find((state) => state.stateName === 'Andhra Pradesh') || states[0];
    setFormStateId(
      selectedStateId || (defaultState ? String(defaultState.id) : ''),
    );
    setCityName('');
    setIsAddCityOpen(true);
  };

  const closeAddCity = () => {
    if (!isCreatingCity) setIsAddCityOpen(false);
  };

  const openEditCity = (city) => {
    setOpenCityMenuId(null);
    setEditingCity(city);
    setEditCityName(city.cityName || '');
  };

  const openServiceableLocations = (city) => {
    setOpenCityMenuId(null);
    setServiceableCity(city);
  };

  const updateServiceableCount = (cityId, selectedCount) => {
    setServiceableCounts((currentCounts) => ({
      ...currentCounts,
      [String(cityId)]: selectedCount,
    }));
  };

  const updateCityStatus = useCallback((cityId, nextStatus) => {
    setCityStatuses((currentStatuses) => {
      const updated = {
        ...currentStatuses,
        [String(cityId)]: nextStatus,
      };
      try {
        window.localStorage.setItem(
          CITY_STATUSES_STORAGE_KEY,
          JSON.stringify(updated),
        );
      } catch {
        // ignore storage error
      }
      return updated;
    });
  }, []);

  const toggleCityStatus = async (city) => {
    const cityId = String(city.id);
    if (updatingCityStatuses[cityId]) return;

    const isCurrentlyActive =
      cityStatuses[cityId] ?? city.isActive ?? true;
    const nextIsActive = !isCurrentlyActive;
    const statusUrl = `${process.env.REACT_APP_REST_API_ENDPOINT}common/${
      nextIsActive ? 'activate-city' : 'Inactive-city'
    }/${cityId}`;

    setUpdatingCityStatuses((currentStatuses) => ({
      ...currentStatuses,
      [cityId]: true,
    }));

    try {
      const response = nextIsActive
        ? await axiosPatchAPI(statusUrl, auth)
        : await axiosDelete(statusUrl, auth?.data?.token);

      if (!response?.isSuccessful || response?.data?.error) {
        const errorMsg =
          response?.data?.message ||
          response?.message ||
          `Unable to ${nextIsActive ? 'activate' : 'deactivate'} city.`;

        if (
          typeof errorMsg === 'string' &&
          errorMsg.toLowerCase().includes('already active')
        ) {
          updateCityStatus(cityId, true);
          toast.info(errorMsg);
          return;
        }

        if (
          typeof errorMsg === 'string' &&
          errorMsg.toLowerCase().includes('already inactive')
        ) {
          updateCityStatus(cityId, false);
          toast.info(errorMsg);
          return;
        }

        toast.error(errorMsg);
        return;
      }

      updateCityStatus(cityId, nextIsActive);
      toast.success(
        response?.data?.message ||
          `City ${nextIsActive ? 'activated' : 'deactivated'} successfully.`,
      );
    } catch (error) {
      toast.error(
        error?.message ||
          `Unable to ${nextIsActive ? 'activate' : 'deactivate'} city.`,
      );
    } finally {
      setUpdatingCityStatuses((currentStatuses) => ({
        ...currentStatuses,
        [cityId]: false,
      }));
    }
  };

  const closeEditCity = () => {
    if (!isUpdatingCity) {
      setEditingCity(null);
      setEditCityName('');
    }
  };

  const handleEditCity = async (event) => {
    event.preventDefault();
    const trimmedCityName = editCityName.trim();
    if (!editingCity?.id || !trimmedCityName || !selectedStateId) return;

    setIsUpdatingCity(true);
    try {
      const response = await axiosUpdate(
        `${process.env.REACT_APP_REST_API_ENDPOINT}common/update-city/${editingCity.id}`,
        { cityName: trimmedCityName },
        auth?.data?.token,
      );

      if (!response?.isSuccessful || response?.data?.error) {
        toast.error(
          response?.data?.message || response?.message || 'Unable to update city.',
        );
        return;
      }

      const nextStateId = String(selectedStateId);
      setEditingCity(null);
      setEditCityName('');
      toast.success(response?.data?.message || 'City updated successfully.');

      try {
        await fetchCities({
          variables: { state: nextStateId },
          fetchPolicy: 'network-only',
        });
      } catch {
        toast.error('City updated, but the city list could not be refreshed.');
      }
    } catch (error) {
      toast.error(error?.message || 'Unable to update city.');
    } finally {
      setIsUpdatingCity(false);
    }
  };

  const handleAddCity = async (event) => {
    event.preventDefault();
    const trimmedCityName = cityName.trim();

    if (!formStateId || !trimmedCityName) return;

    setIsCreatingCity(true);
    try {
      const response = await axiosPost(
        `${process.env.REACT_APP_REST_API_ENDPOINT}common/add-city`,
        {
          stateId: Number(formStateId),
          cityName: trimmedCityName,
        },
        { 'Content-Type': 'application/json' },
        auth,
      );

      if (!response?.isSuccessful || response?.data?.error) {
        toast.error(
          response?.data?.message || response?.message || 'Unable to add city.',
        );
        return;
      }

      const nextStateId = String(formStateId);
      setSelectedStateId(nextStateId);
      setCityName('');
      setIsAddCityOpen(false);
      toast.success(response?.data?.message || 'City added successfully.');

      try {
        await fetchCities({
          variables: { state: nextStateId },
          fetchPolicy: 'network-only',
        });
      } catch {
        toast.error('City added, but the city list could not be refreshed.');
      }
    } catch (error) {
      toast.error(error?.message || 'Unable to add city.');
    } finally {
      setIsCreatingCity(false);
    }
  };

  return (
    <section className="city-service-panel" aria-label="City master">
      <div className="city-service-headings">
        <div className="city-service-state-heading fw-semibold">State</div>
        <div className="city-service-city-heading fw-semibold">
          <span>City</span>
          <Button
            aria-label="Add city"
            className="d-flex align-items-center gap-2 city-master-add-button"
            color="primary"
            disabled={statesLoading || states.length === 0}
            onClick={openAddCity}
            type="button"
          >
            <Plus aria-hidden="true" size={16} />
            <span>Add City</span>
          </Button>
        </div>
      </div>

      <div className="city-service-columns">
        <div className="city-service-state-list" aria-label="States">
          {statesLoading && (
            <div className="city-service-message">Loading states...</div>
          )}
          {statesError && (
            <div className="city-service-message is-error">
              Unable to load states.
            </div>
          )}
          {!statesLoading && !statesError && states.length === 0 && (
            <div className="city-service-message">No states available.</div>
          )}
          {states.map((state) => {
            const stateId = String(state.id);
            const isSelected = stateId === selectedStateId;

            return (
              <div
                className={`city-service-state-row${isSelected ? ' is-selected' : ''}`}
                key={state.id}
              >
                <button
                  aria-pressed={isSelected}
                  className="city-service-state-select"
                  onClick={() => selectState(state)}
                  type="button"
                >
                  <span className="city-service-state-name" title={state.stateName}>
                    {state.stateName}
                  </span>
                  <ChevronRight
                    aria-hidden="true"
                    className="city-service-chevron"
                    size={19}
                  />
                </button>
              </div>
            );
          })}
        </div>

        <div
          className="city-service-city-list"
          aria-label={`Cities in ${selectedState?.stateName || 'selected state'}`}
        >
          <div className="city-service-city-list-inner">
            {citiesLoading && (
              <div className="city-service-message">Loading cities...</div>
            )}
            {citiesError && !citiesLoading && (
              <div className="city-service-message is-error">
                Unable to load cities.
              </div>
            )}
            {!citiesLoading && !citiesError && !selectedState && (
              <div className="city-service-message">
                Select a state to view its cities.
              </div>
            )}
            {!citiesLoading &&
              !citiesError &&
              selectedState &&
              cities.length === 0 && (
                <div className="city-service-message">
                  No cities available for this state.
                </div>
              )}
            {!citiesLoading &&
              !citiesError &&
              cities.map((city) => {
                const cityId = String(city.id);
                const serviceableCount = serviceableCounts[cityId];
                const isCityActive =
                  cityStatuses[cityId] ?? city.isActive ?? true;

                return (
                  <div
                    className="city-service-city-row"
                    key={city.id}
                    onClick={() =>
                      setOpenCityMenuId((currentId) => {
                        return currentId === cityId ? null : cityId;
                      })
                    }
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setOpenCityMenuId((currentId) => {
                          return currentId === cityId ? null : cityId;
                        });
                      }
                    }}
                  >
                    <span className="city-service-city-name" title={city.cityName}>
                      {city.cityName}
                    </span>
                    {Number(serviceableCount) > 0 ? (
                      <span
                        aria-label={`${serviceableCount} serviceable locations`}
                        className="city-service-count-badge"
                        title="Selected serviceable locations"
                      >
                        {serviceableCount}
                      </span>
                    ) : null}
                    <Dropdown
                      direction="down"
                      isOpen={openCityMenuId === cityId}
                      toggle={() =>
                        setOpenCityMenuId((currentId) => {
                          if (currentId === cityId) return null;
                          return cityId;
                        })
                      }
                    >
                      <DropdownToggle
                        aria-label={`Actions for ${city.cityName}`}
                        className="city-master-city-menu-toggle"
                        onClick={(event) => event.stopPropagation()}
                        tag="button"
                        type="button"
                      >
                        <EllipsisVertical aria-hidden="true" size={18} />
                      </DropdownToggle>
                      {ReactDOM.createPortal(
                        <DropdownMenu
                          className="cmn-dropdown city-master-city-menu"
                          style={{ minWidth: '200px', width: '200px' }}
                        >
                          <DropdownItem
                            onClick={() => openServiceableLocations(city)}
                            toggle={false}
                          >
                            <MapPin
                              aria-hidden="true"
                              className="city-master-service-icon"
                              size={16}
                              strokeWidth={2.5}
                            />
                            Serviceable Location
                          </DropdownItem>
                          <DropdownItem
                            onClick={() => openEditCity(city)}
                            toggle={false}
                          >
                            <Pencil aria-hidden="true" size={15} />
                            Edit
                          </DropdownItem>
                          <DropdownItem
                            aria-checked={isCityActive}
                            aria-busy={Boolean(updatingCityStatuses[cityId])}
                            className="city-master-status-toggle"
                            disabled={Boolean(updatingCityStatuses[cityId])}
                            onClick={() => toggleCityStatus(city)}
                            role="switch"
                            toggle={false}
                          >
                            <span className="city-master-status-label">
                              {isCityActive ? (
                                <CheckCircle2
                                  aria-hidden="true"
                                  className="city-master-status-icon is-active"
                                  size={15}
                                />
                              ) : (
                                <XCircle
                                  aria-hidden="true"
                                  className="city-master-status-icon is-inactive"
                                  size={15}
                                />
                              )}
                              <span>
                                {isCityActive ? 'Active' : 'Inactive'}
                              </span>
                            </span>
                            <span
                              aria-hidden="true"
                              className={`city-master-status-switch${
                                isCityActive ? ' is-active' : ''
                              }`}
                            >
                              <span />
                            </span>
                          </DropdownItem>
                        </DropdownMenu>,
                        document.body,
                      )}
                    </Dropdown>
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      <Modal
        centered
        className="city-master-modal"
        isOpen={isAddCityOpen}
        toggle={closeAddCity}
      >
        <form onSubmit={handleAddCity}>
          <ModalHeader toggle={closeAddCity}>Add City</ModalHeader>
          <ModalBody>
            <div className="city-master-form-group">
              <Label className="fw-semibold" htmlFor="city-master-state">
                State <span className="text-danger">*</span>
              </Label>
              <div className="city-master-state-select">
                <Select
                  aria-label="State"
                  classNamePrefix="react-select"
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.value}
                  inputId="city-master-state"
                  isSearchable
                  menuPlacement="bottom"
                  menuPosition="fixed"
                  menuPortalTarget={
                    typeof document === 'undefined' ? null : document.body
                  }
                  maxMenuHeight={220}
                  onChange={(option) => setFormStateId(option?.value || '')}
                  options={stateOptions}
                  placeholder="Select state"
                  styles={STATE_SELECT_STYLES}
                  value={selectedFormState}
                />
              </div>
            </div>
            <div className="city-master-form-group">
              <Label className="fw-semibold" htmlFor="city-master-name">
                City <span className="text-danger">*</span>
              </Label>
              <Input
                className="city-master-form-control"
                id="city-master-name"
                onChange={(event) => setCityName(event.target.value)}
                placeholder="Enter city name"
                required
                type="text"
                value={cityName}
              />
            </div>
          </ModalBody>
          <ModalFooter className="border-0 pb-4 pe-4">
            <Button
              className="px-4 rounded-pill"
              color="secondary"
              disabled={isCreatingCity}
              onClick={closeAddCity}
              outline
              size="sm"
              type="button"
            >
              Cancel
            </Button>
            <Button
              className="px-4 rounded-pill border-0 text-dark"
              color="warning"
              disabled={isCreatingCity || !formStateId || !cityName.trim()}
              size="sm"
              type="submit"
            >
              {isCreatingCity ? 'Adding...' : 'Add City'}
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      <Modal
        centered
        className="city-master-modal"
        isOpen={Boolean(editingCity)}
        toggle={closeEditCity}
      >
        <form onSubmit={handleEditCity}>
          <ModalHeader toggle={closeEditCity}>Edit City</ModalHeader>
          <ModalBody>
            <div className="city-master-form-group">
              <Label className="fw-semibold" htmlFor="city-master-edit-name">
                City <span className="text-danger">*</span>
              </Label>
              <Input
                className="city-master-form-control"
                id="city-master-edit-name"
                onChange={(event) => setEditCityName(event.target.value)}
                placeholder="Enter city name"
                required
                type="text"
                value={editCityName}
              />
            </div>
          </ModalBody>
          <ModalFooter className="border-0 pb-4 pe-4">
            <Button
              className="px-4 rounded-pill"
              color="secondary"
              disabled={isUpdatingCity}
              onClick={closeEditCity}
              outline
              size="sm"
              type="button"
            >
              Cancel
            </Button>
            <Button
              className="px-4 rounded-pill"
              color="primary"
              disabled={isUpdatingCity || !editCityName.trim()}
              size="sm"
              type="submit"
            >
              {isUpdatingCity ? 'Saving...' : 'Save'}
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      <ServiceableLocationsModal
        auth={auth}
        city={serviceableCity}
        isOpen={Boolean(serviceableCity)}
        onClose={() => setServiceableCity(null)}
        onSaved={updateServiceableCount}
        states={states}
      />
    </section>
  );
};

export default CityMaster;
